// frontend/src/components/common/Alert.jsx
import React, { useState, useEffect } from 'react';
import './Alert.css'; // You would create this CSS file
// import Icon from './Icon'; // Assuming you have your Icon component

/**
 * Displays an alert message.
 * @param {'info'|'success'|'warning'|'error'} [type='info'] - The type of alert, affects styling and icon.
 * @param {string|React.ReactNode} message - The message content to display.
 * @param {boolean} [dismissible=false] - If true, shows a close button.
 * @param {function} [onClose] - Callback function when the alert is dismissed by user or auto-dismiss.
 * @param {number} [autoDismissDelay] - If set to a positive number (ms), the alert will auto-dismiss after this delay.
 */
const Alert = ({ 
  type = 'info', 
  message, 
  dismissible = false, 
  onClose, 
  autoDismissDelay 
}) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    let timer;
    if (autoDismissDelay && autoDismissDelay > 0 && isVisible) {
      timer = setTimeout(() => {
        handleClose();
      }, autoDismissDelay);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [autoDismissDelay, isVisible]); // Re-run if visibility changes to restart timer (e.g. if externally controlled)

  const handleClose = () => {
    setIsVisible(false);
    if (typeof onClose === 'function') {
      onClose();
    }
  };

  if (!isVisible || !message) {
    return null;
  }

  // Base class and type-specific class for styling from Alert.css
  const alertClasses = `alert alert-${type} ${dismissible ? 'alert-dismissible' : ''}`;
  
  // Conceptual mapping of type to icon name (if using your Icon component)
  // const iconNameMap = {
  //   info: 'information-circle',
  //   success: 'check-circle',
  //   warning: 'exclamation-triangle',
  //   error: 'exclamation-circle', // Or 'x-circle'
  // };

  // Placeholder using emojis until Icon component is integrated
  const emojiIconMap = {
    info: 'ℹ️',
    success: '✅',
    warning: '⚠️',
    error: '❌',
  };

  return (
    <div className={alertClasses} role="alert">
      <div className="alert-content">
        <span className="alert-icon" aria-hidden="true">
          {/* <Icon name={iconNameMap[type]} /> */}
          {emojiIconMap[type]}
        </span>
        <span className="alert-message">{message}</span>
      </div>
      {dismissible && (
        <button 
          type="button" // Important for buttons not submitting forms
          onClick={handleClose} 
          className="alert-close-button"
          aria-label="Close alert"
        >
          × {/* HTML entity for 'x' (close icon) */}
        </button>
      )}
    </div>
  );
};

export default Alert;